import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3, Settings, Save } from 'lucide-react';

const ForeignExchangeManagement = memo(() => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('balance'); // balance, transfers, returns, reports
  
  // Treasury balance
  const [treasuryBalance, setTreasuryBalance] = useState(null);
  const [foreignPayments, setForeignPayments] = useState([]);
  
  // Form states
  const [transferForm, setTransferForm] = useState({
    montant_dzd: '',
    taux_conversion: '',
    devise: 'EUR',
    agent_name: '',
    partenaire: '',
    reservation_name: '',
    date_operation: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [returnForm, setReturnForm] = useState({
    reste_agent: '',
    date_return: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [selectedPaymentForReturn, setSelectedPaymentForReturn] = useState(null);
  const [showReturnDialog, setShowReturnDialog] = useState(false);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('Fetching foreign exchange data...');
      
      // Get treasury balance
      const balanceResponse = await axios.get(`${API}/treasury/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Treasury balance response:', balanceResponse.data);
      setTreasuryBalance(balanceResponse.data);
      
      // Get foreign payments
      const paymentsResponse = await axios.get(`${API}/foreign-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Foreign payments response:', paymentsResponse.data);
      setForeignPayments(paymentsResponse.data);
      
    } catch (error) {
      console.error('Error fetching foreign exchange data:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show error to user for debugging
      alert('❌ فشل في تحميل بيانات العملات الأجنبية: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Create foreign payment
  const createForeignPayment = async () => {
    if (!transferForm.montant_dzd || !transferForm.taux_conversion || !transferForm.agent_name || !transferForm.partenaire) {
      alert('يرجى إدخال جميع البيانات المطلوبة');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/foreign-payments`, transferForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تسجيل التحويل الخارجي بنجاح');
      setTransferForm({
        montant_dzd: '',
        taux_conversion: '',
        devise: 'EUR',
        agent_name: '',
        partenaire: '',
        reservation_name: '',
        date_operation: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating foreign payment:', error);
      alert('❌ فشل في التحويل: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Return foreign payment
  const returnForeignPayment = async () => {
    if (!returnForm.reste_agent || !selectedPaymentForReturn) {
      alert('يرجى إدخال المبلغ المرجع');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/foreign-payments/${selectedPaymentForReturn.id}/return`, returnForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم تسجيل الإرجاع بنجاح');
      setShowReturnDialog(false);
      setSelectedPaymentForReturn(null);
      setReturnForm({
        reste_agent: '',
        date_return: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error returning foreign payment:', error);
      alert('❌ فشل في الإرجاع: ' + (error.response?.data?.detail || error.message));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Only allow General Accountant and Super Admin
  if (!user || (user.role !== 'general_accountant' && user.role !== 'super_admin')) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-red-600">⚠️ ليس لديك صلاحية للوصول إلى العملات الأجنبية</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💱 إدارة العملات الأجنبية</h1>
          <p className="text-sm text-gray-600 mt-1">تحويل وإدارة العملات الصعبة (يورو - ريال)</p>
        </div>
      </div>

      {/* Multi-Currency Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-green-600">رصيد الدينار الجزائري</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {treasuryBalance?.dzd_balance?.toLocaleString() || 'جاري التحميل...'} دج
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-brand-50 to-cyan-50 border-brand-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-brand-600">رصيد اليورو</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brand-700">
              {treasuryBalance?.eur_balance?.toFixed(2) || 'جاري التحميل...'} €
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-purple-50 to-brand-50 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-purple-600">رصيد الريال السعودي</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {treasuryBalance?.sar_balance?.toFixed(2) || 'جاري التحميل...'} ﷼
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transfer Form */}
      <Card>
        <CardHeader>
          <CardTitle>💱 تحويل خارجي جديد</CardTitle>
          <CardDescription>
            تحويل مبلغ من الدينار الجزائري إلى عملة أجنبية عبر مندوب
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>المبلغ بالدينار الجزائري (دج)</Label>
              <Input
                type="number"
                value={transferForm.montant_dzd}
                onChange={(e) => setTransferForm({...transferForm, montant_dzd: e.target.value})}
                placeholder="المبلغ المراد تحويله"
                min="0"
                step="0.01"
              />
            </div>
            
            <div>
              <Label>نسبة التحويل</Label>
              <Input
                type="number"
                value={transferForm.taux_conversion}
                onChange={(e) => {
                  const rate = e.target.value;
                  setTransferForm({...transferForm, taux_conversion: rate});
                }}
                placeholder="كم دينار = 1 عملة أجنبية"
                min="0"
                step="0.01"
              />
              {transferForm.montant_dzd && transferForm.taux_conversion && (
                <p className="text-xs text-brand-600 mt-1">
                  المبلغ المحول: {(parseFloat(transferForm.montant_dzd) / parseFloat(transferForm.taux_conversion)).toFixed(2)} {transferForm.devise}
                </p>
              )}
            </div>
            
            <div>
              <Label>العملة المستهدفة</Label>
              <select
                value={transferForm.devise}
                onChange={(e) => setTransferForm({...transferForm, devise: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="EUR">💶 يورو (EUR)</option>
                <option value="SAR">🇸🇦 ريال سعودي (SAR)</option>
              </select>
            </div>
            
            <div>
              <Label>اسم المندوب</Label>
              <Input
                value={transferForm.agent_name}
                onChange={(e) => setTransferForm({...transferForm, agent_name: e.target.value})}
                placeholder="اسم المندوب المستلم"
              />
            </div>
            
            <div>
              <Label>الوكيل الخارجي</Label>
              <Input
                value={transferForm.partenaire}
                onChange={(e) => setTransferForm({...transferForm, partenaire: e.target.value})}
                placeholder="اسم الوكيل أو المتعامل"
              />
            </div>
            
            <div>
              <Label>تفاصيل الحجز</Label>
              <Input
                value={transferForm.reservation_name}
                onChange={(e) => setTransferForm({...transferForm, reservation_name: e.target.value})}
                placeholder="اسم الحجز أو التفاصيل"
              />
            </div>
            
            <div>
              <Label>تاريخ العملية</Label>
              <Input
                type="date"
                value={transferForm.date_operation}
                onChange={(e) => setTransferForm({...transferForm, date_operation: e.target.value})}
              />
            </div>
            
            <div>
              <Label>ملاحظات (اختياري)</Label>
              <Input
                value={transferForm.notes}
                onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})}
                placeholder="ملاحظات إضافية"
              />
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={createForeignPayment}
                className="w-full bg-brand-600 hover:bg-brand-700"
                disabled={!transferForm.montant_dzd || !transferForm.taux_conversion || !transferForm.agent_name || !transferForm.partenaire}
              >
                💱 تسجيل التحويل
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Foreign Payments List */}
      <Card>
        <CardHeader>
          <CardTitle>📋 سجل التحويلات الخارجية</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
              </div>
            ) : foreignPayments.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500">لا توجد تحويلات خارجية</p>
                <Button 
                  onClick={fetchData} 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                >
                  🔄 إعادة تحميل
                </Button>
              </div>
            ) : (
              foreignPayments.map((payment) => (
                <div key={payment.id} className="p-4 border border-brand-200 bg-brand-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex flex-col">
                      <span className="font-medium text-brand-800">{payment.reservation_name}</span>
                      <span className="text-sm text-brand-600">
                        👤 {payment.agent_name} → 🏢 {payment.partenaire}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-red-600">
                        -{payment.montant_dzd?.toLocaleString()} دج
                      </span>
                      <div className="text-sm text-brand-600">
                        ↓ {payment.montant_converted?.toFixed(2)} {payment.devise}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">
                      {new Date(payment.date_operation).toLocaleDateString('ar-DZ')}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                        payment.status === 'returned' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-brand-100 text-brand-700'
                      }`}>
                        {payment.status === 'completed' ? '✅ مكتمل' :
                         payment.status === 'returned' ? '🔄 مرجع جزئياً' : '📤 مرسل'}
                      </span>
                      
                      {payment.status === 'sent' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedPaymentForReturn(payment);
                            setShowReturnDialog(true);
                          }}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs"
                        >
                          🔄 تسجيل إرجاع
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔄 تسجيل إرجاع من المندوب</DialogTitle>
            <DialogDescription>
              تسجيل المبلغ المرجع من المندوب بالعملة الأجنبية
            </DialogDescription>
          </DialogHeader>
          
          {selectedPaymentForReturn && (
            <div className="space-y-4">
              {/* Payment Summary */}
              <div className="bg-brand-50 p-4 rounded-lg">
                <h4 className="font-bold text-brand-800 mb-2">تفاصيل التحويل:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>المندوب:</strong> {selectedPaymentForReturn.agent_name}</div>
                  <div><strong>الوكيل الخارجي:</strong> {selectedPaymentForReturn.partenaire}</div>
                  <div><strong>الحجز:</strong> {selectedPaymentForReturn.reservation_name}</div>
                  <div><strong>المبلغ المحول:</strong> {selectedPaymentForReturn.montant_converted?.toFixed(2)} {selectedPaymentForReturn.devise}</div>
                </div>
              </div>

              <div>
                <Label>المبلغ المرجع ({selectedPaymentForReturn.devise})</Label>
                <Input
                  type="number"
                  value={returnForm.reste_agent}
                  onChange={(e) => setReturnForm({...returnForm, reste_agent: e.target.value})}
                  placeholder={`المبلغ المرجع بالـ ${selectedPaymentForReturn.devise}`}
                  max={selectedPaymentForReturn.montant_converted}
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <Label>تاريخ الإرجاع</Label>
                <Input
                  type="date"
                  value={returnForm.date_return}
                  onChange={(e) => setReturnForm({...returnForm, date_return: e.target.value})}
                />
              </div>

              <div>
                <Label>ملاحظات</Label>
                <Input
                  value={returnForm.notes}
                  onChange={(e) => setReturnForm({...returnForm, notes: e.target.value})}
                  placeholder="ملاحظات حول الإرجاع (اختياري)"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowReturnDialog(false)}>
                  إلغاء
                </Button>
                <Button onClick={returnForeignPayment}>
                  🔄 تسجيل الإرجاع
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

// NEW: Tickets Management Component


export default ForeignExchangeManagement;
